import { useState } from "react";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Copy, Check } from "lucide-react";

export const ClipboardTooltip = ({
  value,
  content,
  variant = "outline",
  disableTooltip = false,
  iconOnly = false,
  className = "",
  tooltipText,
}: {
  value: string;
  content?: string;
  variant?:
    | "outline"
    | "secondary"
    | "ghost"
    | "default"
    | "destructive"
    | "link"
    | "subtle"
    | "light"
    | "success"
    | "warning"
    | "info"
    | null
    | undefined;
  disableTooltip?: boolean;
  /**
   * Oculta el `<span>` que muestra `content || value` y deja solo
   * el ícono (Copy / Check). Útil cuando lo que se copia es
   * multilínea (JSON, code snippets) y no conviene renderizar el
   * texto completo en el trigger. Por defecto `false`: el badge
   * sigue mostrando el contenido como hasta ahora.
   */
  iconOnly?: boolean;
  className?: string;
  /** Texto del tooltip antes/después de copiar. Default: "Copiar código" / "¡Copiado!". */
  tooltipText?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);

      if (!disableTooltip) {
        setOpen(true); // ✅ Forzar que el tooltip se abra inmediatamente
      }

      // Reset después de 2 segundos
      setTimeout(() => {
        setCopied(false);
        if (!disableTooltip) {
          setOpen(false);
        }
      }, 2000);
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  const BadgeContent = (
    <Badge
      variant={variant}
      className={`px-2 py-1 text-xs text-foreground/70 cursor-pointer transition-colors ${
        copied
          ? "bg-primary/10  border-primary-300 dark:bg-primary-900 dark:text-primary-400"
          : " dark:hover:bg-background dark:hover:text-primary-400"
      } ${className}`}
      onClick={handleCopy}
    >
      {!iconOnly && (
        <span className="truncate min-w-0 flex-1">{content || value}</span>
      )}
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Badge>
  );

  // Si disableTooltip es true, mostrar solo el badge sin tooltip
  if (disableTooltip) {
    return (
      <div className="flex items-center space-x-2 min-w-0 max-w-full">
        {BadgeContent}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 min-w-0 max-w-full">
      <TooltipProvider>
        <Tooltip open={open} onOpenChange={setOpen}>
          <TooltipTrigger className={`${className} text-foreground`} asChild>
            {BadgeContent}
          </TooltipTrigger>
          <TooltipContent>
            <div>
              {copied
                ? "¡Copiado!"
                : tooltipText ?? "Copiar código"}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
